import { useMemo } from "react";
import {
  Brain, Sparkles, Flag, ShieldCheck, IndianRupee, Camera,
  Replace, Clock, ArrowUpRight,
} from "lucide-react";
import { getActiveProjectGraph } from "../../data/sampleGraph";
import type { GraphNode } from "../../types/ontology";
import { DESIGNER_RULES } from "../../data/designerRules";
import { cls } from "../../lib/cls";
import { formatCompactMoney } from "../../lib/i18n/format";
import { ageDays, daysUntil } from "../../lib/temporal";

/* ProjectAgentHero — the first thing a designer sees on the Agent
 * tab. Replaces a generic "the agent works while you design" block
 * with a live status strip + four metric tiles, every value derived
 * from this project's typed graph. No literal numbers. */

interface Stat {
  id: string;
  icon: JSX.Element;
  label: string;
  value: string;
  detail: string;
  tone: "warn" | "info" | "ok" | "neutral";
  trend?: string;
}

const TONE: Record<Stat["tone"], string> = {
  warn:    "bg-foyr/10  text-foyr-deep border-foyr/30",
  info:    "bg-sky-50   text-sky-800    border-sky-200",
  ok:      "bg-fern/10  text-fern       border-fern/30",
  neutral: "bg-cream-dark text-ink-700  border-border",
};

// Name preserved for existing callers; body delegates to country-aware formatter.
function inrCompact(n: number): string {
  return formatCompactMoney(n);
}

function deriveStats(): { project: GraphNode | undefined; rooms: number; alerts: GraphNode[]; stats: Stat[] } {
  const nodes = getActiveProjectGraph().nodes;
  const project = nodes.find((n) => n.kind === "Project");
  const rooms = nodes.filter((n) => n.kind === "Room").length;
  const alerts = nodes.filter((n) => n.kind === "Alert");
  const skus = nodes.filter((n) => n.kind === "SKU");
  const lines = nodes.filter((n) => n.kind === "BudgetLine");
  const approvals = nodes.filter((n) => n.kind === "Approval");
  const milestones = nodes.filter((n) => n.kind === "Milestone");
  const substitutes = skus.filter((s) =>
    typeof s.attrs["source"] === "string" && (s.attrs["source"] as string).includes("substitute"),
  );

  // Live derivations
  const budgetCap = typeof project?.attrs["budgetCap"] === "number" ? (project.attrs["budgetCap"] as number) : 0;
  const committed = lines.reduce((a, l) => a + (typeof l.attrs["amount"] === "number" ? (l.attrs["amount"] as number) : 0), 0);
  const budgetPct = budgetCap > 0 ? committed / budgetCap : 0;
  const headroom = Math.max(0, budgetCap - committed);

  const longLead = skus.filter((s) => typeof s.attrs["leadDays"] === "number" && (s.attrs["leadDays"] as number) >= 60).length;
  const swapped = substitutes.length;
  const swapSaving = substitutes.reduce((a, sub) => {
    const original = skus.find((s) => s.attrs["source"] && !((s.attrs["source"] as string).includes("substitute")) && s.label.split(" · ")[0] === sub.label.split(" · ")[0]);
    if (!original || typeof original.attrs["price"] !== "number" || typeof sub.attrs["price"] !== "number") return a;
    const delta = (original.attrs["price"] as number) - (sub.attrs["price"] as number);
    return a + Math.max(0, delta);
  }, 0);

  const pendingApprovals = approvals.filter((a) => a.attrs["state"] === "pending");
  const oldestPending = pendingApprovals.reduce((m, a) => Math.max(m, ageDays(a)), 0);

  const dueSoon = milestones.filter((m) => { const d = daysUntil(m); return d >= 0 && d <= 60; }).length;
  const minSlack = milestones.reduce((m, ms) => Math.min(m, Number(ms.attrs["slackDays"] ?? 99)), 99);

  const appliedRules = DESIGNER_RULES.length;
  void appliedRules;

  const stats: Stat[] = [
    {
      id: "alerts",
      icon: <Flag className="w-3.5 h-3.5" />,
      label: "Open alerts",
      value: String(alerts.length),
      detail: alerts.length === 0
        ? "Everything clean."
        : `${alerts.filter((a) => a.attrs["severity"] === "warn").length} warn · ${alerts.filter((a) => a.attrs["severity"] === "info").length} info`,
      tone: alerts.length === 0 ? "ok" : "warn",
    },
    {
      id: "budget",
      icon: <IndianRupee className="w-3.5 h-3.5" />,
      label: "Budget headroom",
      value: inrCompact(headroom),
      detail: `${Math.round(budgetPct * 100)}% committed · cap ${inrCompact(budgetCap)}`,
      tone: budgetPct >= 0.9 ? "warn" : budgetPct >= 0.7 ? "info" : "ok",
    },
    {
      id: "milestones",
      icon: <Clock className="w-3.5 h-3.5" />,
      label: "Milestones in sight",
      value: String(dueSoon),
      detail: minSlack <= 0 ? "Slack exhausted on one." : `Min slack ${minSlack} d`,
      tone: minSlack <= 0 ? "warn" : minSlack <= 3 ? "info" : "ok",
    },
    {
      id: "saved",
      icon: <Sparkles className="w-3.5 h-3.5" />,
      label: swapped > 0 ? "Saved on swaps" : "Long-lead items",
      value: swapped > 0 ? inrCompact(swapSaving) : String(longLead),
      detail: swapped > 0
        ? `${swapped} substitute${swapped === 1 ? "" : "s"} accepted`
        : `${longLead} item${longLead === 1 ? "" : "s"} > 60 d lead`,
      tone: swapped > 0 ? "ok" : longLead > 0 ? "info" : "neutral",
    },
  ];

  // Stash the oldest-pending into the approvals stat as a trend hint
  if (oldestPending >= 3) {
    stats.push({
      id: "approvals",
      icon: <ShieldCheck className="w-3.5 h-3.5" />,
      label: "Approvals waiting",
      value: String(pendingApprovals.length),
      detail: `Oldest ${oldestPending} d — agent nudged`,
      tone: "info",
    });
  }

  return { project, rooms, alerts, stats };
}

export function ProjectAgentHero(): JSX.Element {
  const { project, rooms, alerts, stats } = useMemo(deriveStats, []);
  const projectLabel = project?.label ?? "This project";
  const skuCount = useMemo(() => getActiveProjectGraph().nodes.filter((n) => n.kind === "SKU").length, []);

  return (
    <section className="panel-hero p-6 sm:p-7 relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />
      <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full bg-foyr/12 blur-3xl pointer-events-none" />

      <div className="relative flex flex-col gap-5">
        {/* Header row · live project status */}
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <span className="w-10 h-10 rounded-xl bg-ink-950 text-cream grid place-items-center shrink-0">
              <Brain className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <div className="eyebrow">Designer copilot · {projectLabel}</div>
              <div className="display-2 text-ink-950 mt-0.5 leading-[1.04]">
                <span className="tabular text-foyr">{alerts.length}</span>{" "}
                <span className="text-ink-950">{alerts.length === 1 ? "thing the agent caught" : "things the agent caught"}</span>
                <span className="text-ink-500"> · </span>
                <span className="tabular text-ink-950">{rooms}</span>
                <span className="text-ink-500"> rooms</span>
                <span className="text-ink-500"> · </span>
                <span className="tabular text-ink-950">{skuCount}</span>
                <span className="text-ink-500"> items</span>
              </div>
              <p className="text-[13px] text-ink-700 mt-1.5 max-w-2xl leading-snug">
                Live tally from this project. Every metric below is derived the moment you save, add, or accept a swap — nothing on this card is static.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="pill bg-fern/10 text-fern">
              <span className="w-1.5 h-1.5 rounded-full bg-fern animate-pulse" /> agent online
            </span>
            <a href="/graph" className="pill bg-cream-dark border border-border text-ink-700 hover:text-ink-950 hover:border-foyr/40 transition">
              Project map <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>
        </header>

        {/* Metric tiles — all live, project-derived */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.slice(0, 4).map((s) => (
            <article key={s.id} className={cls(
              "rounded-2xl border bg-cream/95 backdrop-blur-sm px-4 py-3.5 transition-all hover:-translate-y-0.5",
              "hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)]",
            )}>
              <div className="flex items-center justify-between">
                <span className={cls("inline-flex w-6 h-6 rounded-lg border items-center justify-center", TONE[s.tone])}>
                  {s.icon}
                </span>
                <span className="eyebrow !text-[9.5px] !tracking-[0.14em]">{s.label}</span>
              </div>
              <div className="display-3 text-ink-950 leading-none tabular mt-2.5">{s.value}</div>
              <div className="text-[11.5px] text-ink-500 leading-snug mt-1.5">{s.detail}</div>
            </article>
          ))}
        </div>

        {/* What-changed strip — last 3 things, condensed */}
        <RecentSignals />
      </div>
    </section>
  );
}

function RecentSignals(): JSX.Element {
  const items = useMemo(() => {
    const out: { icon: JSX.Element; line: string; tone: Stat["tone"]; when: string }[] = [];

    const alert = getActiveProjectGraph().nodes.find((n) => n.kind === "Alert" && n.attrs["severity"] === "warn");
    if (alert) out.push({
      icon: <Flag className="w-3 h-3" />, tone: "warn",
      line: alert.label,
      when: "just now",
    });

    const sub = getActiveProjectGraph().nodes.find((n) =>
      n.kind === "SKU" && typeof n.attrs["source"] === "string" && (n.attrs["source"] as string).includes("substitute"),
    );
    if (sub) out.push({
      icon: <Replace className="w-3 h-3" />, tone: "info",
      line: `Substitute proposed · ${sub.label}`,
      when: "moments ago",
    });

    const oldPending = getActiveProjectGraph().nodes.find((n) =>
      n.kind === "Approval" && n.attrs["state"] === "pending" && ageDays(n) >= 3,
    );
    if (oldPending) out.push({
      icon: <ShieldCheck className="w-3 h-3" />, tone: "info",
      line: `${oldPending.label} · ${ageDays(oldPending)} d with client`,
      when: "today",
    });

    if (out.length < 3) out.push({
      icon: <Camera className="w-3 h-3" />, tone: "ok",
      line: "Pre-render check passed — style mix within tolerance.",
      when: "before last render",
    });

    return out.slice(0, 3);
  }, []);

  return (
    <div className="rounded-2xl border border-border/60 bg-cream/70 backdrop-blur-sm px-3 py-2.5 flex flex-wrap items-center gap-2">
      <span className="eyebrow shrink-0 mr-1">Latest</span>
      {items.map((it, i) => (
        <span
          key={i}
          className={cls(
            "inline-flex items-center gap-1.5 pl-1.5 pr-2 py-1 rounded-lg border text-[11.5px]",
            TONE[it.tone],
          )}
        >
          <span className="w-4 h-4 rounded-md grid place-items-center bg-cream/80">{it.icon}</span>
          <span className="truncate max-w-[260px]">{it.line}</span>
          <span className="text-[10px] font-mono opacity-70 ml-1">{it.when}</span>
        </span>
      ))}
    </div>
  );
}
